import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import "remixicon/fonts/remixicon.css";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";
import { formatDateTime } from "../../utils/adminFormat";

interface ModelProvider {
  id: number;
  name: string;
  provider_type: string;
  base_url: string;
  status: boolean;
  priority: number;
}

interface ModelConfig {
  id: number;
  provider_id: number;
  model_name: string;
  display_name: string;
  model_type: string;
  max_tokens: number;
  context_window: number;
  input_price_per1k: number;
  output_price_per1k: number;
  input_price_per_1k?: number;
  output_price_per_1k?: number;
  is_public: boolean;
  is_default: boolean;
  supports_tool_calls?: boolean;
  supports_image?: boolean;
  status: boolean;
  weight: number;
  rate_limit: number;
  daily_limit: number;
}

interface ModelEndpoint {
  id: number;
  model_config_id: number;
  provider_id?: number;
  actual_model_name?: string;
  base_url: string;
  status: string;
  weight: number;
  priority: number;
  input_price_per_1k: number;
  output_price_per_1k: number;
  error_count: number;
  last_error_at?: string;
  last_success_at?: string;
  health_check_at?: string;
  last_test_at?: string;
  last_test_error?: string;
  last_notified_at?: string;
}

interface ModelUsageStat {
  modelName: string;
  requestCount: number;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  totalCost: number;
}

type ModalKind = "provider" | "config" | "endpoint";
type ModalMode = "create" | "edit";

const providerTypes = ["openai", "azure", "anthropic", "deepseek", "zhipu", "moonshot", "qwen", "doubao", "custom"];
const modelTypes = ["chat", "embedding", "rerank", "image"];
const { Text } = Typography;

const tokensToK = (value?: number) => (value == null ? value : Number((value / 1000).toFixed(3)));
const kToTokens = (value?: number) => (value == null ? value : Math.round(Number(value) * 1000));
const storedPriceToPerMillion = (value?: number) => value;
const perMillionToStoredPrice = (value?: number) => value;

const inputPricePer1k = (record: ModelConfig) => record.input_price_per_1k ?? record.input_price_per1k ?? 0;
const outputPricePer1k = (record: ModelConfig) => record.output_price_per_1k ?? record.output_price_per1k ?? 0;

const ModelManagement: React.FC = () => {
  const { API_BASE_URL } = useGlobal();
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState<ModelProvider[]>([]);
  const [configs, setConfigs] = useState<ModelConfig[]>([]);
  const [modelUsageStats, setModelUsageStats] = useState<ModelUsageStat[]>([]);
  const [endpoints, setEndpoints] = useState<ModelEndpoint[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<number>();
  const [modalKind, setModalKind] = useState<ModalKind>("provider");
  const [modalMode, setModalMode] = useState<ModalMode>("create");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>();
  const [submitting, setSubmitting] = useState(false);
  const [checkingEndpointId, setCheckingEndpointId] = useState<number>();
  const [changingEndpointId, setChangingEndpointId] = useState<number>();
  const [form] = Form.useForm();

  const providerOptions = useMemo(
    () => providers.map((item) => ({ label: item.name, value: item.id })),
    [providers]
  );

  const configOptions = useMemo(
    () =>
      configs.map((item) => ({
        label: item.display_name ? `${item.display_name} (${item.model_name})` : item.model_name,
        value: item.id,
      })),
    [configs]
  );

  const selectedConfig = useMemo(
    () => configs.find((item) => item.id === selectedConfigId),
    [configs, selectedConfigId]
  );

  const usageByModelName = useMemo(() => {
    const result = new Map<string, ModelUsageStat>();
    modelUsageStats.forEach((item) => result.set(item.modelName, item));
    return result;
  }, [modelUsageStats]);

  const fetchProviders = useCallback(async () => {
    const response = await request.get(`${API_BASE_URL}/api/v3/admin/models/providers`);
    if (response.data.code === 200) setProviders(response.data.data || []);
  }, [API_BASE_URL]);

  const fetchConfigs = useCallback(async () => {
    const response = await request.get(`${API_BASE_URL}/api/v3/admin/models/configs`);
    if (response.data.code === 200) setConfigs(response.data.data || []);
  }, [API_BASE_URL]);

  const fetchModelUsageStats = useCallback(async () => {
    const response = await request.get(`${API_BASE_URL}/api/v3/admin/stats/models`);
    if (response.data.code === 200) setModelUsageStats(response.data.data || []);
  }, [API_BASE_URL]);

  const fetchEndpoints = useCallback(
    async (modelConfigId?: number) => {
      if (!modelConfigId) {
        setEndpoints([]);
        return;
      }
      const response = await request.get(`${API_BASE_URL}/api/v3/admin/models/endpoints`, {
        params: { modelConfigId },
      });
      if (response.data.code === 200) setEndpoints(response.data.data || []);
    },
    [API_BASE_URL]
  );

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchProviders(), fetchConfigs(), fetchModelUsageStats()]);
      if (selectedConfigId) await fetchEndpoints(selectedConfigId);
    } catch (error) {
      message.error("获取模型配置失败");
    } finally {
      setLoading(false);
    }
  }, [fetchConfigs, fetchEndpoints, fetchModelUsageStats, fetchProviders, selectedConfigId]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!selectedConfigId && configs.length > 0) {
      setSelectedConfigId(configs[0].id);
    }
  }, [configs, selectedConfigId]);

  useEffect(() => {
    fetchEndpoints(selectedConfigId);
  }, [fetchEndpoints, selectedConfigId]);

  const openModal = (kind: ModalKind, mode: ModalMode, record?: any) => {
    setModalKind(kind);
    setModalMode(mode);
    setEditingRecord(record);
    form.resetFields();
    if (record) form.setFieldsValue(toFormValues(kind, record));
    if (kind === "endpoint" && !record) {
      form.setFieldsValue({
        modelConfigId: selectedConfigId,
        actualModelName: selectedConfig?.model_name,
        weight: 1,
        priority: 0,
        inputPricePer1k: 0,
        outputPricePer1k: 0,
      });
    }
    if (kind === "provider" && !record) form.setFieldsValue({ providerType: "openai", status: true, priority: 0 });
    if (kind === "config" && !record) form.setFieldsValue({ modelType: "chat", isPublic: true, isDefault: false, supportsToolCalls: false, supportsImage: false, status: true, weight: 1 });
    setModalOpen(true);
  };

  const toFormValues = (kind: ModalKind, record: any) => {
    if (kind === "provider") {
      return {
        name: record.name,
        providerType: record.provider_type,
        baseUrl: record.base_url,
        status: record.status,
        priority: record.priority,
      };
    }
    if (kind === "config") {
      return {
        providerId: record.provider_id,
        modelName: record.model_name,
        displayName: record.display_name,
        modelType: record.model_type,
        maxTokens: tokensToK(record.max_tokens),
        contextWindow: tokensToK(record.context_window),
        inputPricePer1k: storedPriceToPerMillion(record.input_price_per_1k ?? record.input_price_per1k),
        outputPricePer1k: storedPriceToPerMillion(record.output_price_per_1k ?? record.output_price_per1k),
        isPublic: record.is_public,
        isDefault: record.is_default,
        supportsToolCalls: record.supports_tool_calls,
        supportsImage: record.supports_image,
        status: record.status,
        weight: record.weight,
        rateLimit: record.rate_limit,
        dailyLimit: record.daily_limit,
      };
    }
    return {
      modelConfigId: record.model_config_id,
      providerId: record.provider_id || undefined,
      actualModelName: record.actual_model_name,
      baseUrl: record.base_url,
      weight: record.weight,
      priority: record.priority,
      inputPricePer1k: storedPriceToPerMillion(record.input_price_per_1k),
      outputPricePer1k: storedPriceToPerMillion(record.output_price_per_1k),
    };
  };

  const normalizeSubmitValues = (kind: ModalKind, values: any) => {
    if (kind === "config") {
      return {
        ...values,
        maxTokens: kToTokens(values.maxTokens),
        contextWindow: kToTokens(values.contextWindow),
        inputPricePer1k: perMillionToStoredPrice(values.inputPricePer1k),
        outputPricePer1k: perMillionToStoredPrice(values.outputPricePer1k),
      };
    }
    if (kind === "endpoint") {
      return {
        ...values,
        inputPricePer1k: perMillionToStoredPrice(values.inputPricePer1k),
        outputPricePer1k: perMillionToStoredPrice(values.outputPricePer1k),
      };
    }
    return values;
  };

  const submitModal = async () => {
    const values = normalizeSubmitValues(modalKind, await form.validateFields());
    setSubmitting(true);
    try {
      const id = editingRecord?.id;
      if (modalKind === "provider") {
        const payload = modalMode === "edit" ? { id, ...values } : values;
        await request.post(
          `${API_BASE_URL}/api/v3/admin/models/providers${modalMode === "edit" ? "/update" : ""}`,
          payload
        );
      } else if (modalKind === "config") {
        const payload = modalMode === "edit" ? { id, ...values } : values;
        await request.post(
          `${API_BASE_URL}/api/v3/admin/models/configs${modalMode === "edit" ? "/update" : ""}`,
          payload
        );
      } else {
        const payload = modalMode === "edit" ? { id, ...values } : values;
        await request.post(
          `${API_BASE_URL}/api/v3/admin/models/endpoints${modalMode === "edit" ? "/update" : ""}`,
          payload
        );
      }
      message.success("保存成功");
      setModalOpen(false);
      await refreshAll();
    } catch (error: any) {
      if (!error?.errorFields) message.error(error.response?.data?.msg || "保存失败");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteRecord = async (kind: ModalKind, id: number) => {
    const path = kind === "provider" ? "providers" : kind === "config" ? "configs" : "endpoints";
    Modal.confirm({
      title: "确认删除",
      okButtonProps: { danger: true },
      onOk: async () => {
        await request.post(`${API_BASE_URL}/api/v3/admin/models/${path}/delete`, { id });
        message.success("已删除");
        await refreshAll();
      },
    });
  };

  const checkEndpoint = async (id: number) => {
    setCheckingEndpointId(id);
    try {
      const response = await request.post(`${API_BASE_URL}/api/v3/admin/models/endpoints/check`, { id });
      if (response.data.code === 200) {
        const result = response.data.data;
        message[result?.healthy ? "success" : "warning"](result?.healthy ? "端点健康" : result?.message || "端点异常");
        await fetchEndpoints(selectedConfigId);
      } else {
        message.error(response.data.msg || "检查失败");
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "检查失败");
    } finally {
      setCheckingEndpointId(undefined);
    }
  };

  const updateEndpointStatus = async (id: number, status: "enabled" | "disabled") => {
    setChangingEndpointId(id);
    try {
      const response = await request.post(`${API_BASE_URL}/api/v3/admin/models/endpoints/status`, { id, status });
      if (response.data.code === 200) {
        message.success(status === "enabled" ? "测试通过，已启用" : "已停用");
        await fetchEndpoints(selectedConfigId);
      } else {
        message.error(response.data.msg || "状态更新失败");
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "状态更新失败");
    } finally {
      setChangingEndpointId(undefined);
    }
  };

  const renderEndpointStatus = (value: string) => {
    const color = value === "enabled" ? "success" : value === "unhealthy" ? "error" : "default";
    const text = value === "enabled" ? "健康" : value === "unhealthy" ? "异常" : "停用";
    return <Tag color={color}>{text}</Tag>;
  };

  const providerColumns: TableProps<ModelProvider>["columns"] = [
    { title: "名称", dataIndex: "name" },
    { title: "类型", dataIndex: "provider_type" },
    { title: "Base URL", dataIndex: "base_url", ellipsis: true },
    { title: "状态", dataIndex: "status", render: (value) => <Tag color={value ? "success" : "default"}>{value ? "启用" : "停用"}</Tag> },
    { title: "优先级", dataIndex: "priority" },
    { title: "操作", render: (_, record) => <Space><Button size="small" onClick={() => openModal("provider", "edit", record)}>编辑</Button><Button danger size="small" onClick={() => deleteRecord("provider", record.id)}>删除</Button></Space> },
  ];

  const configColumns: TableProps<ModelConfig>["columns"] = [
    { title: "模型", dataIndex: "model_name", render: (_, record) => <Space><span>{record.display_name || record.model_name}</span>{record.is_default && <Tag color="processing">默认</Tag>}{record.is_public && <Tag color="blue">公开</Tag>}</Space> },
    { title: "类型", dataIndex: "model_type" },
    { title: "上下文(k)", dataIndex: "context_window", render: (value?: number) => tokensToK(value) ?? "-" },
    { title: "最大输出(k)", dataIndex: "max_tokens", render: (value?: number) => tokensToK(value) ?? "-" },
    { title: "调用次数", render: (_, record) => usageByModelName.get(record.model_name)?.requestCount ?? 0 },
    { title: "累计Token", render: (_, record) => (usageByModelName.get(record.model_name)?.totalTokens ?? 0).toLocaleString() },
    { title: "能力", render: (_, record) => <Space>{record.supports_tool_calls && <Tag color="geekblue">Tools</Tag>}{record.supports_image && <Tag color="purple">Image</Tag>}{!record.supports_tool_calls && !record.supports_image && <Text type="secondary">-</Text>}</Space> },
    { title: "价格(元/百万)", render: (_, record) => `${storedPriceToPerMillion(inputPricePer1k(record)) ?? 0}/${storedPriceToPerMillion(outputPricePer1k(record)) ?? 0}` },
    { title: "状态", dataIndex: "status", render: (value) => <Tag color={value ? "success" : "default"}>{value ? "启用" : "停用"}</Tag> },
    { title: "操作", render: (_, record) => <Space><Button size="small" onClick={() => setSelectedConfigId(record.id)}>端点</Button><Button size="small" onClick={() => openModal("config", "edit", record)}>编辑</Button><Button danger size="small" onClick={() => deleteRecord("config", record.id)}>删除</Button></Space> },
  ];

  const endpointColumns: TableProps<ModelEndpoint>["columns"] = [
    {
      title: "真实模型",
      dataIndex: "actual_model_name",
      width: 160,
      render: (value, record) => value || configs.find((item) => item.id === record.model_config_id)?.model_name || "-",
    },
    {
      title: "提供商",
      dataIndex: "provider_id",
      width: 120,
      render: (value?: number) => providers.find((item) => item.id === value)?.name || "继承模型",
    },
    { title: "Base URL", dataIndex: "base_url", ellipsis: true },
    { title: "优先级", dataIndex: "priority" },
    { title: "权重", dataIndex: "weight" },
    { title: "价格覆盖(元/百万)", render: (_, record) => `${storedPriceToPerMillion(record.input_price_per_1k) || 0}/${storedPriceToPerMillion(record.output_price_per_1k) || 0}` },
    { title: "状态", dataIndex: "status", render: renderEndpointStatus },
    { title: "错误", dataIndex: "error_count" },
    { title: "最近测试", dataIndex: "last_test_at", render: (value) => formatDateTime(value) },
    { title: "测试错误", dataIndex: "last_test_error", ellipsis: true, render: (value) => value || "-" },
    {
      title: "操作",
      fixed: "right",
      render: (_, record) => (
        <Space wrap>
          <Button size="small" loading={checkingEndpointId === record.id} onClick={() => checkEndpoint(record.id)}>测试</Button>
          {record.status === "enabled" ? (
            <Button size="small" loading={changingEndpointId === record.id} onClick={() => updateEndpointStatus(record.id, "disabled")}>停用</Button>
          ) : (
            <Button size="small" type="primary" loading={changingEndpointId === record.id} onClick={() => updateEndpointStatus(record.id, "enabled")}>测试并启用</Button>
          )}
          <Button size="small" onClick={() => openModal("endpoint", "edit", record)}>编辑</Button>
          <Button danger size="small" onClick={() => deleteRecord("endpoint", record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  const renderModalForm = () => {
    if (modalKind === "provider") {
      return <><Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item><Form.Item name="providerType" label="类型" rules={[{ required: true }]}><Select options={providerTypes.map((value) => ({ label: value, value }))} disabled={modalMode === "edit"} /></Form.Item><Form.Item name="baseUrl" label="Base URL" rules={[{ required: true }]}><Input /></Form.Item><Form.Item name="apiKey" label="API Key"><Input.Password /></Form.Item><Form.Item name="orgId" label="Org ID"><Input /></Form.Item><Form.Item name="priority" label="优先级"><InputNumber style={{ width: "100%" }} /></Form.Item><Form.Item name="status" label="启用" valuePropName="checked"><Switch /></Form.Item></>;
    }
    if (modalKind === "config") {
      return <><Form.Item name="providerId" label="供应商" rules={[{ required: true }]}><Select options={providerOptions} /></Form.Item><Form.Item name="modelName" label="公开模型标识" rules={[{ required: true }]}><Input /></Form.Item><Form.Item name="displayName" label="显示名"><Input /></Form.Item><Form.Item name="modelType" label="类型"><Select options={modelTypes.map((value) => ({ label: value, value }))} /></Form.Item><Form.Item name="maxTokens" label="最大输出(k)"><InputNumber min={0} step={0.1} style={{ width: "100%" }} /></Form.Item><Form.Item name="contextWindow" label="上下文窗口(k)"><InputNumber min={0} step={0.1} style={{ width: "100%" }} /></Form.Item><Form.Item name="inputPricePer1k" label="输入价格(元/百万tokens)"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item><Form.Item name="outputPricePer1k" label="输出价格(元/百万tokens)"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item><Form.Item name="weight" label="权重"><InputNumber style={{ width: "100%" }} /></Form.Item><Form.Item name="rateLimit" label="分钟限流"><InputNumber style={{ width: "100%" }} /></Form.Item><Form.Item name="dailyLimit" label="日限额"><InputNumber style={{ width: "100%" }} /></Form.Item><Space wrap><Form.Item name="isPublic" label="公开" valuePropName="checked"><Switch /></Form.Item><Form.Item name="isDefault" label="默认" valuePropName="checked"><Switch /></Form.Item><Form.Item name="supportsToolCalls" label="Tool Calls" valuePropName="checked"><Switch /></Form.Item><Form.Item name="supportsImage" label="图片" valuePropName="checked"><Switch /></Form.Item><Form.Item name="status" label="启用" valuePropName="checked"><Switch /></Form.Item></Space></>;
    }
    return <><Form.Item name="modelConfigId" label="公开模型" rules={[{ required: true }]}><Select options={configOptions} /></Form.Item><Form.Item name="providerId" label="提供商覆盖"><Select options={providerOptions} allowClear placeholder="继承公开模型提供商" /></Form.Item><Form.Item name="actualModelName" label="真实模型名"><Input placeholder="为空则使用公开模型名" /></Form.Item><Form.Item name="baseUrl" label="Base URL"><Input placeholder="为空则继承提供商 Base URL" /></Form.Item><Form.Item name="apiKey" label="API Key"><Input.Password placeholder="为空则继承提供商 API Key" /></Form.Item><Form.Item name="priority" label="优先级"><InputNumber style={{ width: "100%" }} /></Form.Item><Form.Item name="weight" label="权重"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item><Form.Item name="inputPricePer1k" label="输入成本覆盖(元/百万tokens)"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item><Form.Item name="outputPricePer1k" label="输出成本覆盖(元/百万tokens)"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item></>;
  };

  return (
    <div style={{ background: "#f0f2f5", minHeight: "100%", borderRadius: 8, padding: 16 }}>
      <Card title={<Space><i className="ri-cpu-line" style={{ color: "#1677ff" }} />模型管理</Space>} extra={<Button onClick={refreshAll}>刷新</Button>} variant="outlined">
        <Tabs
          items={[
            { key: "providers", label: "供应商", children: <><Button type="primary" style={{ marginBottom: 12 }} onClick={() => openModal("provider", "create")}>新增供应商</Button><Table rowKey="id" loading={loading} columns={providerColumns} dataSource={providers} /></> },
            { key: "configs", label: "模型", children: <><Button type="primary" style={{ marginBottom: 12 }} onClick={() => openModal("config", "create")}>新增模型</Button><Table rowKey="id" loading={loading} columns={configColumns} dataSource={configs} /></> },
            { key: "endpoints", label: "端点", children: <><Space style={{ marginBottom: 12 }}><Select style={{ width: 280 }} options={configOptions} value={selectedConfigId} onChange={setSelectedConfigId} /><Button type="primary" onClick={() => openModal("endpoint", "create")}>新增端点</Button></Space><Table rowKey="id" loading={loading} columns={endpointColumns} dataSource={endpoints} /></> },
          ]}
        />
      </Card>
      <Modal title={modalMode === "create" ? "新增" : "编辑"} open={modalOpen} onOk={submitModal} onCancel={() => setModalOpen(false)} confirmLoading={submitting} width={640} destroyOnClose>
        <Form form={form} layout="vertical">{renderModalForm()}</Form>
      </Modal>
    </div>
  );
};

export default ModelManagement;